import React, { createContext, Dispatch, useContext, useReducer, useRef } from 'react';
import { useCustomEventListener } from 'react-custom-events';

import { type DeviceSettings } from '@ledium/types';

import lediumApi from '../../../utils/lediumApi';
import { withShallowBailout } from '../../withShallowBailout';
import {
    GlobalAction,
    SET_DATE_TIME_FORMAT,
    SET_IS_ONLINE,
    SET_OFFLINE_MODE,
    SET_SERVER_TIME_OFFSET,
    SET_SOCKET_CONNECTED,
    useGlobalActions,
    USER_LOGOUT_API_SUCCESS
} from '../actions';
import initialGlobalState from '../init';
import { getSocketIo } from '../init/socketIo';
import { globalReducers } from '../reducers';
import { GlobalState } from '../state/initialGlobalState';

interface GlobalActionsContextValue {
    globalDispatch: Dispatch<GlobalAction>;
    globalActions: ReturnType<typeof useGlobalActions>;
}

const GlobalStateContext = createContext<GlobalState | undefined>(undefined);
const GlobalActionsContext = createContext<GlobalActionsContextValue | undefined>(undefined);

function useGlobalStore() {
    const state = useContext(GlobalStateContext);
    const actionsCtx = useContext(GlobalActionsContext);
    if (state === undefined || actionsCtx === undefined) {
        throw new Error('useGlobalStore must be used inside GlobalStoreProvider');
    }
    return { globalState: state, globalDispatch: actionsCtx.globalDispatch, globalActions: actionsCtx.globalActions };
}

function useGlobalStoreActions() {
    const ctx = useContext(GlobalActionsContext);
    if (ctx === undefined) {
        throw new Error('useGlobalStoreActions must be used inside GlobalStoreProvider');
    }
    return ctx;
}

const GlobalStoreProvider = ({ children }: { children: React.ReactNode }) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const reducer = React.useCallback(withShallowBailout(globalReducers), []);
    const [state, dispatch] = useReducer(reducer, initialGlobalState);

    const stateRef = useRef(state);
    stateRef.current = state;

    // Actions are stable — created once on mount, read current state via stateRef
    const stableActions = React.useMemo(() => useGlobalActions(() => stateRef.current, dispatch), [dispatch]);

    const actionsContextValue = React.useMemo<GlobalActionsContextValue>(
        () => ({ globalDispatch: dispatch, globalActions: stableActions }),
        [dispatch, stableActions]
    );

    React.useEffect(() => {
        let isMounted = true;
        lediumApi.get<DeviceSettings>('settings/').then((data) => {
            if (isMounted) {
                const clientTime = Date.now();
                dispatch({ type: SET_DATE_TIME_FORMAT, payload: { dateFormat: data?.dateFormat ?? null, timeFormat: data?.timeFormat ?? null } });
                dispatch({ type: SET_OFFLINE_MODE, payload: data?.offlineMode ?? false });
                dispatch({ type: SET_SERVER_TIME_OFFSET, payload: (data?.serverTime ?? clientTime) - clientTime });
            }
        });
        return () => {
            isMounted = false;
        };
    }, []);

    React.useEffect(() => {
        const socket = getSocketIo();
        const onConnectivity = ({ isOnline }: { isOnline: boolean }) => {
            dispatch({ type: SET_IS_ONLINE, payload: isOnline });
        };
        socket.on('connectivity', onConnectivity);
        return () => {
            socket.off('connectivity', onConnectivity);
        };
    }, []);

    React.useEffect(() => {
        const socket = getSocketIo();
        const onConnect = () => dispatch({ type: SET_SOCKET_CONNECTED, payload: true });
        const onDisconnect = () => dispatch({ type: SET_SOCKET_CONNECTED, payload: false });
        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('connect_error', onDisconnect);
        return () => {
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('connect_error', onDisconnect);
        };
    }, []);

    useCustomEventListener('lediumUnauthorized', () => {
        dispatch({ type: USER_LOGOUT_API_SUCCESS });
    });

    return (
        <GlobalActionsContext.Provider value={actionsContextValue}>
            <GlobalStateContext.Provider value={state}>{children}</GlobalStateContext.Provider>
        </GlobalActionsContext.Provider>
    );
};

export { GlobalStateContext, GlobalActionsContext, useGlobalStore, useGlobalStoreActions, GlobalStoreProvider };
